import { graphql } from 'gatsby';
import React from 'react';
import PropTypes from 'prop-types';

import HeroBanner from '~/components/blocks/acf/HeroBanner';

import { useSiteSettings } from '~/hooks/useSiteSettings';

const HeroBannerBlock = ({
    attributes,
    globalBlockOptions: { globalBlockOptions },
    blockHeroBanner: {
        variant,
        includeGraphics,
        backgroundImage,
        title,
        copy,
        ctasVariant,
        ctas
    }
}) => {
    const { appLinks } = useSiteSettings();

    return (
        <HeroBanner
            {...{
                variant,
                includeGraphics,
                backgroundImage,
                title,
                copy,
                ctasVariant,
                ctas,
                appLinks,
                globalBlockOptions,
                attributes
            }}
        />
    );
};

HeroBannerBlock.propTypes = {
    blockHeroBanner: PropTypes.shape({
        variant: PropTypes.string,
        includeGraphics: PropTypes.bool,
        backgroundImage: PropTypes.shape({}),
        title: PropTypes.string.isRequired,
        copy: PropTypes.string,
        ctasVariant: PropTypes.string,
        ctas: PropTypes.arrayOf(
            PropTypes.shape({
                icon: PropTypes.string,
                link: PropTypes.shape({
                    url: PropTypes.string.isRequired,
                    title: PropTypes.string
                })
            })
        )
    }).isRequired
};

export default HeroBannerBlock;

export const fragment = graphql`
    fragment HeroBannerBlockFragment on WpAcfHeroBannerBlock {
        name
        attributes {
            anchor
            className
        }
        globalBlockOptions {
            globalBlockOptions {
                marginBottom
            }
        }
        blockHeroBanner {
            variant
            includeGraphics
            backgroundImage {
                altText
                sourceUrl
                localFile {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                }
            }
            title
            copy
            ctasVariant
            ctas {
                icon
                link {
                    url
                    title
                }
            }
        }
    }
`;

export const exampleData = [
    {
        variant: 'Default Hero Banner - Blue',
        description:
            'Should be used at the top of a page. Optionally include background graphics. Title is required.',
        props: {
            name: 'acf/hero-banner',
            blockHeroBanner: {
                variant: 'blue',
                title: 'Hero Banner Title',
                copy: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                includeGraphics: true
            }
        }
    },
    {
        variant: 'Hero Banner - Blue, No Graphics',
        props: {
            name: 'acf/hero-banner',
            blockHeroBanner: {
                variant: 'blue',
                title: 'Hero Banner Title',
                includeGraphics: false
            }
        }
    },
    {
        variant: 'Hero Banner - Photo with Text Box',
        description:
            'Recommended Image Size for all variants: 2.28 Aspect Ratio, Minimum 2560px x 1100px. Optional icon with CTA',
        props: {
            name: 'acf/hero-banner',
            blockHeroBanner: {
                variant: 'photo-text-box',
                title: 'Hero Banner Title',
                copy: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                includeGraphics: true,
                backgroundImage: {
                    sourceUrl: 'https://picsum.photos/2560/1100?random=1'
                },
                ctas: [
                    {
                        icon: 'garage',
                        link: {
                            title: 'Learn More',
                            url: '#1'
                        }
                    }
                ]
            }
        }
    },
    {
        variant: 'Hero Banner - Large Photo Dark',
        props: {
            name: 'acf/hero-banner',
            blockHeroBanner: {
                variant: 'large-photo-dark',
                title: 'Hero Banner Longer Longer Title',
                includeGraphics: true,
                backgroundImage: {
                    sourceUrl: 'https://picsum.photos/2560/1100?random=2'
                }
            }
        }
    },
    {
        variant: 'Hero Banner - Large Photo Dark with App Downloads',
        props: {
            name: 'acf/hero-banner',
            blockHeroBanner: {
                variant: 'large-photo-dark',
                title: 'Hero Banner Title',
                includeGraphics: true,
                backgroundImage: {
                    sourceUrl: 'https://picsum.photos/2560/1100?random=3'
                },
                ctasVariant: 'appLinks'
            }
        }
    },
    {
        variant: 'Hero Banner - Large Photo Light',
        props: {
            name: 'acf/hero-banner',
            blockHeroBanner: {
                variant: 'large-photo-light',
                title: 'Hero Banner Title',
                copy: 'Sed ut perspiciatis, unde omnis iste natus error sit voluptatem',
                includeGraphics: false,
                backgroundImage: {
                    sourceUrl: 'https://picsum.photos/id/37/2560/1100'
                },
                ctas: [
                    {
                        link: {
                            title: 'View Promotion',
                            url: '#2'
                        }
                    }
                ]
            }
        }
    }
];
